<template>
  <div class="menu-list-container">
    <!-- 顶部品牌Logo -->
    <!-- <div class="menu-header">
      <div class="brand-logo-section">
        <img src="@/assets/ochrm-logo.png" alt="OChem Logo" class="logo-image" />
      </div>
    </div> -->

    <!-- 菜单列表 -->
    <nav class="menu-navigation" v-if="!loading">
      <ul class="menu-items">
        <li
          v-for="item in displayMenuItems"
          :key="item.id"
          class="menu-item"
          :class="{ active: isActive(item.path) }"
        >
          <a
            :href="item.path"
            class="menu-link"
            @click.prevent="handleMenuClick(item)"
          >
            {{ getMenuName(item) }}
          </a>
        </li>
      </ul>
    </nav>

    <!-- 加载状态 -->
    <div v-if="loading" class="menu-loading">
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>{{ $t("menu.loading") || "Loading..." }}</span>
    </div>

    <!-- 底部联系信息 -->
    <div class="menu-footer">
      <div class="contact-logo">
        <img
          src="@/assets/static/ochrm-logo.png"
          alt="OChem Logo"
          class="footer-logo"
        />
      </div>

      <div class="contact-details">
        <div class="contact-item">
          <span class="contact-label">Tel</span>
          <span class="contact-value">+86-13362222290</span>
        </div>

        <div class="contact-item">
          <span class="contact-label">Address</span>
          <span class="contact-value">
            688 Xifeng Road, Huzhou City, Zhejiang Province, China
          </span>
        </div>

        <div class="contact-item">
          <span class="contact-label">Email</span>
          <span class="contact-value">info@ochem.biz</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Loading } from "@element-plus/icons-vue";
import { menuApi } from "@/api/menu";

export default {
  name: "MenuList",
  components: {
    Loading,
  },
  props: {
    // 是否使用默认菜单（当API失败时）
    useFallback: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["menu-click", "menu-loaded"],
  data() {
    return {
      menuItems: [],
      loading: false,
      currentPath: this.$route?.path || "/",
    };
  },
  computed: {
    // 显示的菜单项（过滤、排序）
    displayMenuItems() {
      return this.menuItems
        .filter((item) => item.show !== false && item.status !== 0)
        .sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
    },
  },
  watch: {
    "$route.path"(newPath) {
      this.currentPath = newPath;
    },
    // 监听语言变化，重新加载菜单
    "$i18n.locale"() {
      this.loadMenus();
    },
  },
  async mounted() {
    await this.loadMenus();
  },
  methods: {
    /**
     * 从API加载菜单数据
     */
    async loadMenus() {
      this.loading = true;

      try {
        const response = await menuApi.getPublicMenusCatalog();
        // 处理后端返回的数据结构（可能是数组或包装对象）
        const data = Array.isArray(response) ? response : response?.data || [];

        if (data.length > 0) {
          this.menuItems = this.processApiMenuData(data);
        } else if (this.useFallback) {
          // 如果API返回空数据且允许使用后备方案，使用默认菜单
          this.useDefaultMenus();
        }
        this.$emit("menu-loaded", this.menuItems);
      } catch (error) {
        console.error("加载菜单失败:", error);

        if (this.useFallback) {
          // API失败时使用默认菜单
          this.useDefaultMenus();
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * 处理API返回的菜单数据
     * @param {Array} apiData - API返回的菜单数据
     * @returns {Array} 处理后的菜单项数组
     */
    processApiMenuData(apiData) {
      return apiData.map((menu) => {
        // 从path获取i18n键（去掉第一个斜杠）
        let i18nKey = menu.path.replace(/^\//, "");
        // 特殊处理：首页路径"/"对应"home"键
        if (i18nKey === "") {
          i18nKey = "home";
        }

        // 根据当前语言获取对应的菜单名称
        const displayName = this.getMenuNameFromI18n(i18nKey) || menu.name;

        return {
          id: menu.id,
          name: displayName, // 使用i18n翻译后的名称
          path: menu.path,
          orderNo: menu.orderNo || 0,
          show: menu.show,
          status: menu.status,
          i18nKey, // 保存i18n键用于后续处理
        };
      });
    },

    /**
     * 从i18n获取菜单名称
     * @param {string} i18nKey - i18n键
     * @returns {string|null} 翻译值，如果不存在则返回null
     */
    getMenuNameFromI18n(i18nKey) {
      try {
        // 获取当前语言
        const currentLocale = this.$i18n?.locale || "zh-CN";
        console.log("当前语言:", currentLocale);

        // 方法1: 尝试通过$t函数获取翻译
        if (this.$t) {
          const translation = this.$t(`nav.${i18nKey}`);
          // console.log('通过$t获取翻译 - 键:', `nav.${i18nKey}`, '值:', translation);
          if (translation && translation !== `nav.${i18nKey}`) {
            return translation;
          }
        }
      } catch (error) {
        console.warn(`从i18n获取菜单名称失败: key=${i18nKey}`, error);
      }
      return null;
    },

    /**
     * 使用默认菜单（后备方案）
     */
    useDefaultMenus() {
      const defaultMenuConfig = [
        { id: "home", path: "/", orderNo: 0, show: 1, status: 1 },
        { id: "materials", path: "/materials", orderNo: 1, show: 1, status: 1 },
        {
          id: "solventfree",
          path: "/solventfree",
          orderNo: 2,
          show: 1,
          status: 1,
        },
        {
          id: "estersolvent",
          path: "/estersolvent",
          orderNo: 3,
          show: 1,
          status: 1,
        },
        {
          id: "alcoholicsolvent",
          path: "/alcoholicsolvent",
          orderNo: 4,
          show: 1,
          status: 1,
        },
        { id: "support", path: "/support", orderNo: 5, show: 1, status: 1 },
        { id: "news", path: "/news", orderNo: 6, show: 1, status: 1 },
        { id: "hr", path: "/hr", orderNo: 7, show: 1, status: 1 },
        { id: "about", path: "/about", orderNo: 8, show: 1, status: 1 },
        { id: "contact", path: "/contact", orderNo: 9, show: 1, status: 1 },
      ];

      this.menuItems = defaultMenuConfig.map((menu) => {
        // 从path获取i18n键（去掉第一个斜杠）
        let i18nKey = menu.path.replace(/^\//, "");
        // 特殊处理：首页路径"/"对应"home"键
        if (i18nKey === "") {
          i18nKey = "home";
        }

        // 根据当前语言获取对应的菜单名称
        const displayName =
          this.getMenuNameFromI18n(i18nKey) ||
          this.getFallbackMenuName(i18nKey);

        return {
          id: menu.id,
          name: displayName,
          path: menu.path,
          orderNo: menu.orderNo,
          show: menu.show,
          status: menu.status,
          i18nKey,
        };
      });
    },

    /**
     * 获取后备菜单名称（当i18n中没有对应翻译时使用）
     * @param {string} i18nKey - i18n键
     * @returns {string} 后备名称
     */
    getFallbackMenuName(i18nKey) {
      const fallbackNames = {
        home: "首页",
        materials: "新型碳材料",
        solventfree: "无溶剂粘合剂",
        estersolvent: "酯溶剂粘合剂",
        alcoholicsolvent: "醇溶剂粘合剂",
        support: "技术支持",
        news: "新闻中心",
        hr: "人力资源",
        about: "关于我们",
        contact: "联系我们",
      };
      return fallbackNames[i18nKey] || i18nKey;
    },

    /**
     * 获取菜单名称（根据当前语言）
     */
    getMenuName(item) {
      // 现在菜单项的name字段已经根据当前语言设置好了
      return item.name || "";
    },

    /**
     * 判断菜单项是否激活
     */
    isActive(path) {
      if (!path) return false;

      // 精确匹配
      if (this.currentPath === path) return true;

      // 首页特殊处理
      if (path === "/" && this.currentPath === "/") return true;

      // 子路径匹配（但排除首页）
      if (path !== "/" && this.currentPath.startsWith(path)) return true;

      return false;
    },

    /**
     * 处理菜单点击事件
     */
    handleMenuClick(item) {
      // 触发自定义事件
      this.$emit("menu-click", item);

      // 导航到对应路径
      if (item.path && item.path !== this.currentPath) {
        this.$router.push(item.path);
      }
    },
  },
};
</script>

<style scoped>
.menu-list-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(180deg, #2c2c2c 0%, #1a1a1a 100%);
  color: #ffffff;
  overflow-y: auto;
}

/* 顶部Logo区域 */
.menu-header {
  padding: 40px 20px 30px;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.brand-logo-section {
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo-image {
  max-width: 180px;
  height: auto;
  filter: brightness(1.1);
}

/* 菜单导航区域 */
.menu-navigation {
  flex: 1;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.menu-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.menu-item {
  position: relative;
}
.menu-link {
  display: block;
  padding: 14px 24px;
  color: #b6cdf0; /* 浅蓝色，接近超链接 */
  /* 移除下划线 */
  text-decoration: none;
  font-size: 18px;
  font-weight: 400;
  text-align: center;
  transition: color 0.2s ease;
  letter-spacing: 0.3px;
  cursor: pointer;
}

.menu-link:hover,
.menu-link:focus {
  color: #ffffff;
  outline: none;
}

.menu-item.active .menu-link {
  color: #ffffff;
  font-weight: 600;
}
/* 加载状态 */
.menu-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

/* 底部联系信息区域 */
.menu-footer {
  text-align: center;
  padding: 30px 40px 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.contact-logo {
  text-align: center;
  margin-bottom: 25px;
}

.footer-logo {
  max-width: 140px;
  height: auto;
  opacity: 0.9;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.contact-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .menu-list-container {
    height: auto;
    max-height: 80vh;
    overflow-y: auto;
  }

  .menu-header {
    padding: 25px 15px 20px;
  }

  .logo-image {
    max-width: 140px;
  }

  .menu-navigation {
    padding: 30px 0;
  }

  .menu-card {
    width: 260px;
  }

  .menu-link {
    padding: 12px 18px;
    font-size: 16px;
  }

  .menu-footer {
    padding: 20px 25px 30px;
  }

  .footer-logo {
    max-width: 120px;
  }

  .contact-item {
    gap: 3px;
  }

  .contact-label {
    font-size: 11px;
  }

  .contact-value {
    font-size: 12px;
  }
}

/* 滚动条样式 */
.menu-list-container::-webkit-scrollbar {
  width: 6px;
}

.menu-list-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.menu-list-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.menu-list-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
